import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { identityApiRef, useApi } from "@backstage/core-plugin-api";
import { type Nullable } from "@internal/backstage-plugin-utility-common";

function isAuthError(e: Error): boolean {
    return /401|403|unauthorized|forbidden/i.test(e.message);
}

/**
 * Re-checks Backstage identity on every route change.
 * Returns `isAuthenticated`, a transient `authError` (network failures etc.),
 * and a `retry` function to re-run the check without a navigation event.
 * Only calls `onUnauthenticated` for genuine auth failures (401/403), not
 * for transient errors that should show a retry state instead.
 */
export const useIsAuthenticated = (
    onUnauthenticated?: () => void
): {
    isAuthenticated: boolean;
    authError: Nullable<Error>;
    retry: () => void;
} => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authError, setAuthError] = useState<Nullable<Error>>(null);
    const [retryCount, setRetryCount] = useState(0);
    const identityApi = useApi(identityApiRef);
    const location = useLocation();

    const retry = useCallback(() => setRetryCount(c => c + 1), []);

    useEffect(() => {
        let ignore = false;
        setAuthError(null);
        identityApi
            .getBackstageIdentity()
            .then(() => {
                if (!ignore) setIsAuthenticated(true);
            })
            .catch((e: unknown) => {
                if (ignore) return;
                const error = e instanceof Error ? e : new Error(String(e));
                if (isAuthError(error)) {
                    setIsAuthenticated(false);
                    onUnauthenticated?.();
                } else {
                    setAuthError(error);
                }
            });
        return () => {
            ignore = true;
        };
    }, [identityApi, location, onUnauthenticated, retryCount]);

    return { isAuthenticated, authError, retry };
};
